import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { fetchEvents } from "../lib/api";
import { Stack, ArrowLeft, Users, ChartLineUp } from "@phosphor-icons/react";
import { formatUSD, formatPct } from "../lib/format";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents({ limit: 30 })
      .then((d) => setEvents(d.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="events-page">
      <Header />
      <div className="mx-auto max-w-[1400px] px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Stack size={22} weight="duotone" className="text-edge" />
          <h1 className="font-display text-3xl font-medium">Multi-Outcome Events</h1>
        </div>
        <p className="text-sm text-zinc-400 font-body max-w-2xl mb-10">
          Bundles of related markets (elections, championships, etc). Open one to see AI ranking
          across all outcomes — find the most mispriced contender.
        </p>

        {loading ? (
          <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading events…</div>
        ) : events.length === 0 ? (
          <div className="text-zinc-500 font-body">No events available.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {events.map((e) => {
              // Top 3 outcomes by YES price
              const top = [...(e.markets || [])]
                .sort((a, b) => (b.yesPrice || 0) - (a.yesPrice || 0))
                .slice(0, 3);
              return (
                <Link
                  key={e.id}
                  to={`/event/${e.id}`}
                  data-testid={`event-card-${e.id}`}
                  className="group border border-white/10 hover:border-white/30 bg-zinc-950/40 hover:bg-zinc-900/60 p-5 transition-colors flex flex-col"
                >
                  <div className="flex items-start gap-3 mb-4">
                    {e.image && (
                      <img src={e.image} alt="" className="w-12 h-12 object-cover border border-white/10 grayscale group-hover:grayscale-0 transition-all" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500 mb-1">
                        {e.category || "Event"} · {e.marketCount} outcomes
                      </div>
                      <h3 className="font-display text-base font-medium leading-snug line-clamp-2 text-zinc-100 group-hover:text-white">
                        {e.title}
                      </h3>
                    </div>
                  </div>

                  <div className="space-y-1.5 mt-auto mb-4">
                    {top.map((m) => (
                      <div key={m.id} className="flex items-center justify-between text-sm font-body">
                        <span className="text-zinc-300 truncate pr-2">
                          {m.groupItemTitle || m.question}
                        </span>
                        <span className="font-mono tabular-nums text-zinc-200">
                          {formatPct(m.yesPrice, 0)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between text-[11px] font-mono text-zinc-500 tabular-nums border-t border-white/5 pt-3">
                    <span className="inline-flex items-center gap-1">
                      <ChartLineUp size={11} /> {formatUSD(e.volume)} vol
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users size={11} /> {e.marketCount}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
